import React, { createContext, useState, useMemo, useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import { delay } from 'lodash';
import { scrollTo } from 'utils/general';

export const VehicleDetailsSearchContext = createContext();

const getNextScrollToResultsIndex = ({ scrollToResultsIndex, searchResults }) => {
  if (scrollToResultsIndex === null || scrollToResultsIndex + 1 === searchResults.length) {
    return 0;
  }
  return scrollToResultsIndex + 1;
};

const getPreviousScrollToResultsIndex = ({ scrollToResultsIndex, searchResults }) => {
  if (scrollToResultsIndex === null || scrollToResultsIndex === 0) {
    return searchResults.length - 1;
  }
  return scrollToResultsIndex - 1;
};

export const VehicleDetailsSearchProvider = ({ children, value }) => {
  const [isHeaderCollapsed, setIsHeaderCollapsed] = useState(null);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [scrollToResultsIndex, setScrollToResultsIndex] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const [searchResultsCount, setSearchResultsCount] = useState(0);
  const [vehicleDetailsSearchString, setVehicleDetailsSearchString] = useState('');

  useEffect(() => {
    if (!vehicleDetailsSearchString || vehicleDetailsSearchString === '') {
      setSearchResultsCount(0);
    }
    setSearchResults([]);
    setScrollToResultsIndex(0);
  }, [vehicleDetailsSearchString]);

  useEffect(() => {
    setSearchResultsCount(searchResults?.length);
    if (searchResults.length > 0) {
      // scroll after slight delay
      delay(() => {
        const firstItem = searchResults[0];
        if (firstItem?.fieldTarget) {
          scrollTo({ scrollToId: firstItem.fieldTarget });
        }
      }, 50);
    }
  }, [searchResults]);

  const clearSearch = () => {
    setIsSearchOpen(false);
    setScrollToResultsIndex(null);
    setSearchResultsCount(0);
    setVehicleDetailsSearchString('');
    setSearchResults([]);
  };

  const getActiveResultId = () => {
    return searchResults[scrollToResultsIndex || 0]?.fieldId;
  };

  const getCurrentPosition = () => scrollToResultsIndex + 1;

  const goToNextResult = ({ direction }) => {
    if (searchResults.length === 0) {
      return;
    }
    const newScrollToResultsIndex =
      direction === 'previous'
        ? getPreviousScrollToResultsIndex({ scrollToResultsIndex, searchResults })
        : getNextScrollToResultsIndex({ scrollToResultsIndex, searchResults });
    scrollTo({
      scrollToId: searchResults[newScrollToResultsIndex].fieldTarget,
    });
    setScrollToResultsIndex(newScrollToResultsIndex);
  };

  const contextValue = useMemo(
    () => ({
      clearSearch,
      getActiveResultId,
      getCurrentPosition,
      goToNextResult,
      isHeaderCollapsed,
      isSearchOpen,
      searchResults,
      searchResultsCount,
      setIsHeaderCollapsed,
      setIsSearchOpen,
      setSearchResults,
      setSearchResultsCount,
      setVehicleDetailsSearchString,
      vehicleDetailsSearchString,
    }),
    [
      clearSearch,
      getActiveResultId,
      getCurrentPosition,
      goToNextResult,
      isHeaderCollapsed,
      isSearchOpen,
      searchResults,
      searchResultsCount,
      setIsHeaderCollapsed,
      setIsSearchOpen,
      setSearchResults,
      setSearchResultsCount,
      setVehicleDetailsSearchString,
      vehicleDetailsSearchString,
    ],
  );

  return (
    <VehicleDetailsSearchContext.Provider value={value || contextValue}>
      {children}
    </VehicleDetailsSearchContext.Provider>
  );
};

VehicleDetailsSearchProvider.displayName = 'ExpandedRowsProvider';
VehicleDetailsSearchProvider.propTypes = {
  children: PropTypes.node,
  value: PropTypes.object,
};

export const useVehicleDetailsSearch = () => useContext(VehicleDetailsSearchContext);
